/**
 * Edit component for Points Display Block
 *
 * Shows a realistic preview of what the block will look like on the checkout.
 */
// biome-ignore lint/style/useImportType: Required for JSX
import React from "react";
import { __ } from "@wordpress/i18n";
import { useBlockProps, InspectorControls, PanelColorSettings } from "@wordpress/block-editor";
import { PanelBody, ToggleControl, TextControl, SelectControl } from "@wordpress/components";

interface BlockAttributes {
	enabled: boolean;
	pointsText: string;
	showZeroPoints: boolean;
	invitationText: string;
	invitationCta: string;
	buttonVariant: "text" | "contained" | "outlined";
	buttonFullWidth: boolean;
	buttonBackgroundColor: string;
	buttonTextColor: string;
	buttonBorderColor: string;
}

interface EditProps {
	attributes: BlockAttributes;
	setAttributes: (attributes: Partial<BlockAttributes>) => void;
}

const Edit: React.FC<EditProps> = ({ attributes, setAttributes }) => {
	const blockProps = useBlockProps({
		className: "leat-points-display",
	});

	// Helper to replace placeholders with example values for preview
	const replacePlaceholders = (text: string) => {
		return text
			.replace(/\{\{\s*points\s*\}\}/g, "150")
			.replace(/\{\{\s*credits_currency\s*\}\}/g, "points");
	};

	// Render the actual UI that customers will see
	const renderPreview = () => {
		if (!attributes.enabled) {
			return (
				<div style={{ padding: "1rem", textAlign: "center", opacity: 0.6 }}>
					{__(
						"Points display is disabled. Enable it in the sidebar settings to show the preview.",
						"leat",
					)}
				</div>
			);
		}

		// Show invitation preview with button
		return (
			<div className="wc-block-components-totals-wrapper" style={{ padding: "1rem", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
				<div className="leat-points-text" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
					<svg
						className="leat-points-icon"
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="20 12 20 22 4 22 4 12" />
						<rect x="2" y="7" width="20" height="5" />
						<line x1="12" y1="22" x2="12" y2="7" />
						<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
						<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
					</svg>
					<span>{replacePlaceholders(attributes.invitationText || attributes.pointsText)}</span>
				</div>

				<button
					type="button"
					className={`wc-block-components-button wp-element-button ${attributes.buttonVariant}`}
					style={{
						width: attributes.buttonFullWidth ? "100%" : "auto",
						padding: "10px 20px",
						cursor: "pointer",
						...(attributes.buttonBackgroundColor && { backgroundColor: attributes.buttonBackgroundColor }),
						...(attributes.buttonTextColor && { color: attributes.buttonTextColor }),
						...(attributes.buttonBorderColor && { borderColor: attributes.buttonBorderColor }),
					}}
					disabled
				>
					{attributes.invitationCta}
				</button>
			</div>
		);
	};

	return (
		<>
			<InspectorControls>
				<PanelBody title={__("Display Settings", "leat-crm")} initialOpen={true}>
					<ToggleControl
						label={__("Enable points display", "leat-crm")}
						help={__(
							"Show customers how many points they will earn from their order on the checkout page.",
							"leat",
						)}
						checked={attributes.enabled}
						onChange={(enabled) => setAttributes({ enabled })}
					/>

					<ToggleControl
						label={__("Show zero points", "leat-crm")}
						help={__(
							"If enabled, the points display will show even when the customer will earn 0 points.",
							"leat",
						)}
						checked={attributes.showZeroPoints}
						onChange={(showZeroPoints) => setAttributes({ showZeroPoints })}
					/>
				</PanelBody>

				<PanelBody title={__("Text Settings", "leat-crm")} initialOpen={false}>
					<TextControl
						label={__("Points display text", "leat-crm")}
						help={__(
							"The text displayed on checkout showing points the customer will earn. You can use: {{ points }}, {{ credits_currency }}",
							"leat",
						)}
						value={attributes.pointsText}
						onChange={(pointsText) => setAttributes({ pointsText })}
					/>

					<TextControl
						label={__("Points invitation text", "leat-crm")}
						help={__(
							"Text shown to non-members who could earn points by joining. Placeholders: {{ points }}, {{ credits_currency }}",
							"leat",
						)}
						value={attributes.invitationText}
						onChange={(invitationText) => setAttributes({ invitationText })}
					/>

					<TextControl
						label={__("Invitation button text", "leat-crm")}
						help={__(
							"The text for the 'join program' call-to-action button shown to non-members.",
							"leat",
						)}
						value={attributes.invitationCta}
						onChange={(invitationCta) => setAttributes({ invitationCta })}
					/>
				</PanelBody>

				<PanelBody title={__("Button Settings", "leat-crm")} initialOpen={false}>
					<SelectControl
						label={__("Button style", "leat-crm")}
						help={__(
							"Choose the visual style for the join program button.",
							"leat",
						)}
						value={attributes.buttonVariant}
						options={[
							{ label: __("Outlined", "leat-crm"), value: "outlined" },
							{ label: __("Contained", "leat-crm"), value: "contained" },
							{ label: __("Text", "leat-crm"), value: "text" },
						]}
						onChange={(buttonVariant) => setAttributes({ buttonVariant: buttonVariant as "text" | "contained" | "outlined" })}
					/>

					<ToggleControl
						label={__("Full width button", "leat-crm")}
						help={__(
							"If enabled, the button will stretch to fill the full width of the container.",
							"leat",
						)}
						checked={attributes.buttonFullWidth}
						onChange={(buttonFullWidth) => setAttributes({ buttonFullWidth })}
					/>
				</PanelBody>

				{/* @ts-ignore - WordPress PanelColorSettings types may be incomplete */}
				<PanelColorSettings
					title={__("Button Colors", "leat-crm")}
					colorSettings={[
						{
							value: attributes.buttonBackgroundColor,
							onChange: (buttonBackgroundColor: string) => setAttributes({ buttonBackgroundColor }),
							label: __("Background Color", "leat-crm"),
						},
						{
							value: attributes.buttonTextColor,
							onChange: (buttonTextColor: string) => setAttributes({ buttonTextColor }),
							label: __("Text Color", "leat-crm"),
						},
						{
							value: attributes.buttonBorderColor,
							onChange: (buttonBorderColor: string) => setAttributes({ buttonBorderColor }),
							label: __("Border Color", "leat-crm"),
						},
					]}
				/>
			</InspectorControls>

			<div {...blockProps}>{renderPreview()}</div>
		</>
	);
};

export default Edit;
